import type { ReactNode } from 'react';
import type { TableAdapter } from '../../ports/table-adapter';
import type { UrlAdapter } from '../../ports/url-adapter';
import type { ActionContext, TableAction } from '../actions/action';
import type { ColumnDef } from '../schema/column';

export interface RowDragEvent<T> {
    row: T;
    from: number;
    to: number;
    rows: T[];
}

export interface EmptyStateAction {
    label: string;
    icon?: ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
    disabled?: boolean;
    tooltip?: string;
}

export interface EmptyStateConfig {
    title?: string;
    description?: ReactNode;
    noResultsTitle?: string;
    noResultsDescription?: ReactNode;
    entityName?: string;
    icon?: ReactNode;
    noResultsIcon?: ReactNode;
    action?: EmptyStateAction;
}

export interface SubTable<TParent, TChild> {
    columns: ColumnDef<TChild>[];
    data: (parent: TParent) => TChild[] | TableAdapter<TChild>;
    getRowId?: (row: TChild) => string;

    id?: string;
    header?: string;
    count?: (parent: TParent) => number;
    enabled?: (parent: TParent) => boolean;
    selection?: false | 'single' | 'multi';
    selectable?: (row: TChild) => boolean;
    onSelectionChange?: (rows: TChild[], ids: string[]) => void;
    actions?: TableAction<TChild>[];
    emptyState?: EmptyStateConfig;
    entityName?: string;
    onRowDragEnd?: (e: RowDragEvent<TChild>) => void;
}

export enum TablePadding {
    DEFAULT = 'default',
    NONE = 'none',
}

export interface TableProps<T> {
    id: string;
    description: string;
    'data-testid'?: string;

    columns: ColumnDef<T>[];

    adapter: TableAdapter<T>;
    getRowId?: (row: T) => string;

    selection?: false | 'single' | 'multi';
    selectable?: (row: T) => boolean;
    onSelectionChange?: (rows: T[], ids: string[]) => void;

    // eslint-disable-next-line typescript/no-explicit-any -- child row type is inferred from data/columns/getRowId at the call site; `unknown` would make a correctly-typed child column fail to assign
    subTable?: SubTable<T, any>;

    actions?: TableAction<T>[] | ((ctx: ActionContext<T>) => TableAction<T>[]);
    toolbarSlot?: ReactNode;
    hideSearch?: boolean;
    searchPlaceholder?: string;

    emptyState?: EmptyStateConfig;
    entityName?: string;
    onRowClick?: (row: T) => void;
    onRowDragEnd?: (e: RowDragEvent<T>) => void;

    urlSync?: false | UrlAdapter;
    padding?: TablePadding;
}
